import * as Api from '@/request/api/defaultApi';
import QRCode from 'qrcode';
import { createOrder360, cancelPay360, queryOrderStatus360, isSDK360Ready, isSDK360Inited, friendlySDK360Error } from '@/utils/SDK360';
import type { SDK360OrderStatus } from '@/utils/SDK360';
import { ensureSDK360Inited } from '@/composables/useSDK360Init';

/**
 * 360 联运支付会话的宿主回调。
 * composable 只负责「创建订单 → 生成二维码 → 监听状态 → 取消轮询」这套与宿主无关的编排，
 * 具体把二维码/价格/状态写到哪（本地 ref 还是 pinia state）由各调用方通过这些钩子决定。
 */
export interface SDK360PayHooks {
    onQr: (dataUrl: string) => void;        // 二维码图片就绪（dataURL）
    onPrice: (price: string) => void;       // 价格就绪（单位：元，两位小数）
    onPaid: () => void | Promise<void>;     // 支付成功（order_status 20/30/50）
    onClosed: () => void;                   // 已取消 / 交易关闭（order_status 60/70）
    onTimeout: () => void;                  // 二维码超时未支付
    onError: (msg: string) => void;         // 下单失败 / 二维码生成失败
    onStillPending?: () => void;            // 「我已完成支付」主动查询后仍未到账
    timeout?: number;                       // 二维码有效期（ms），默认 5 分钟
}

// 主动查询后，等待 SDK 回调的反馈时限（ms）。超过仍无回调即视为「尚未到账」给用户提示。
const QUERY_FEEDBACK_MS = 4000;

/**
 * 360 联运支付编排。确认购买页（views/Purchase）与首页支付（store/Purchase）共用这一套，
 * 避免两处各写一遍 createOrder360 回调（statusCallback 分层、cancelPay 传 360 订单号等坑只需维护一处）。
 *
 * 用法：const pay = useSDK360Payment(); pay.start(product, hooks); pay.queryNow(); pay.stop();
 * 每个调用方各持有一个独立会话（各自的 sdkOrderId / 超时计时器），互不干扰。
 */
export function useSDK360Payment() {
    const appStore = useAppStore();

    // 360 侧订单 id（responseCallback 回传）。cancelPay 必须传这个，不能传厂商订单号——
    // SDK 内部的轮询表就是以它为 key 的，传错会静默失效（cancelPay 恒返回 0）。
    let sdkOrderId = '';
    let userId = '';                        // 主动查询 getOrderStatus 需要
    let timeoutTimer: any = null;
    let hooks: SDK360PayHooks | null = null;
    let settled = false;                    // 幂等标志：已进入终态（成功/取消），防止重复跳转

    /** 停止当前订单的底层轮询与超时计时 */
    function stop() {
        if (sdkOrderId) {
            cancelPay360(sdkOrderId);
            sdkOrderId = '';
        }
        if (timeoutTimer) {
            clearTimeout(timeoutTimer);
            timeoutTimer = null;
        }
    }

    /**
     * 统一处理订单状态回调（createOrder 内部轮询与 queryNow 主动查询共用），保证行为一致且幂等。
     * 业务数据在 res.data.data，比文档多包一层。
     */
    async function handleStatus(res: SDK360OrderStatus) {
        console.log('[SDK360] statusCallback', res);
        if (res.errno !== 0) {
            console.error('[SDK360] 查询订单状态异常', res.errmsg);
            return;
        }
        if (settled) return;

        const status = Number(res.data?.data?.order_status);
        if (status === 20 || status === 30 || status === 50) {
            settled = true;
            stop();
            await hooks?.onPaid();
        } else if (status === 60 || status === 70) {
            // 已取消 / 交易关闭
            settled = true;
            stop();
            hooks?.onClosed();
        }
    }

    /**
     * 发起一笔 360 支付：后端创建订单 → SDK 下单 → 内部轮询状态。
     * @returns 是否成功进入「等待支付」状态（后端下单成功即为 true）
     */
    async function start(product: any, h: SDK360PayHooks): Promise<boolean> {
        // 清理上一笔订单的底层轮询与超时计时
        stop();
        hooks = h;
        settled = false;
        userId = '';

        if (!isSDK360Ready()) {
            console.error('[SDK360] SDK360.js 未加载，无法下单');
            h.onError('支付组件未就绪，请重启软件后重试');
            return false;
        }

        // 启动时 init 可能失败（如 get360Config 请求超时），此时 appid/qid 为空，
        // SDK 所有接口都会返回 110001「请先调用init方法进行初始化！」。
        // 这里按需补一次 init 自愈，成功即继续下单，用户无感；ensureSDK360Inited 内部有并发去重。
        if (!isSDK360Inited()) {
            console.warn('[SDK360] 下单前发现 SDK 未初始化，尝试补充初始化');
            if (!await ensureSDK360Inited()) {
                console.error('[SDK360] 补充初始化失败，无法下单（检查 get360Config 是否可达）');
                h.onError('支付服务初始化失败，请检查网络后重试');
                return false;
            }
            console.log('[SDK360] 补充初始化成功，继续下单');
        }

        // 1. 向后端创建订单，拿到 SDK360.createOrder 所需入参
        let result: any;
        try {
            result = await Api.create360Order({
                token: appStore.UserInfo?.token || '',
                product_token: product.product_token,
                channel: appStore.ClientInfo?.client_channel,
                client_version: appStore.ClientInfo?.client_version,
                uuid: appStore.ClientInfo?.client_guid,
                api_version: '1.0',
                // two_purchase 非必填，不传
            });
        } catch (err) {
            // 网络异常 / 接口非 2xx：http.ts 会 reject 响应体，这里必须兜住，否则冒泡成 Uncaught (in promise)
            console.error('[SDK360] 创建订单失败', err);
            h.onError('创建订单失败，请稍后重试');
            return false;
        }

        if (result.code != 200 || !result.data) {
            h.onError(result.msg || '创建订单失败，请重试');
            return false;
        }

        const order = result.data;
        userId = String(order.user_id);
        h.onPrice((Number(order.order_amount) / 100).toFixed(2)); // 后端返回分，展示元

        // 2. 调用 360 SDK 创建订单（SDK 内部自动轮询订单状态）
        createOrder360(
            {
                order_id: order.order_id,
                order_amount: Number(order.order_amount),
                create_time: String(order.create_time),
                user_id: String(order.user_id),
                product_id: order.product_id,
                product_name: order.product_name,
                // 与官方 demo 对齐：付费单固定 0，不开代扣，task_id 传空串
                order_pay_type: 0,
                auto_pay_status: 0,
                task_id: '',
            },
            // responseCallback：业务数据只包一层（res.data.xxx）
            async (res) => {
                console.log('[SDK360] responseCallback', res);
                const data = typeof res.data === 'object' ? res.data : null;
                // 先记下 360 侧订单 id，cancelPay / getOrderStatus 都要用它（SDK 紧接着就会用它起轮询）
                if (data?.order_id) sdkOrderId = String(data.order_id);

                if (res.errno === 0 && data?.qr_code) {
                    try {
                        h.onQr(await QRCode.toDataURL(data.qr_code, { margin: 1, width: 240 }));
                    } catch (err) {
                        console.error('[SDK360] 二维码生成失败', err);
                        h.onError('二维码生成失败，请刷新重试');
                    }
                } else {
                    // 不可直接透传 res.errmsg：SDK 本地错误文案是开发者向的（如「请先调用init方法进行初始化！」），
                    // 甚至可能是 Error 对象。统一由 friendlySDK360Error 归一化，原始信息只进 console。
                    h.onError(friendlySDK360Error(res, '下单失败，请刷新重试'));
                }
            },
            // statusCallback：SDK 内部轮询驱动
            handleStatus
        );

        // 二维码超时刷新（停止底层轮询并通知宿主显示刷新图标）
        timeoutTimer = setTimeout(() => {
            stop();
            h.onTimeout();
        }, h.timeout ?? 300000);

        return true;
    }

    /**
     * 「我已完成支付」主动查询一次订单状态。
     *
     * getOrderStatus 与 createOrder 的内部轮询是同一函数、共用 queryList[order_id] 槽位，
     * 直接叠加调用会导致孤儿定时器指数增长；故先 cancelPay 收敛成单链，再以 handleStatus 重新驱动，
     * 保证任何时刻只有一条轮询链。
     *
     * @returns true=已触发查询；false=当前无进行中的订单（宿主应提示二维码已失效）
     */
    function queryNow(): boolean {
        if (settled || !sdkOrderId) return false;

        // 收敛现有轮询链，避免与手动查询叠加
        cancelPay360(sdkOrderId);

        let responded = false;
        queryOrderStatus360({ order_id: Number(sdkOrderId), user_id: userId }, (res) => {
            responded = true;
            // 未初始化等 SDK 本地错误会走到这里且不含订单状态，handleStatus 只 log 不误判
            handleStatus(res);
        });

        // 待付款时 getOrderStatus 不回调，用短超时给「尚未到账」反馈（轮询链仍在后台继续）
        setTimeout(() => {
            if (!responded && !settled) hooks?.onStillPending?.();
        }, QUERY_FEEDBACK_MS);

        return true;
    }

    return { start, queryNow, stop };
}
